import { useRouter } from "next/router"

import { SOFIA_THREAD_QUERY_PARAM } from "@/constants/sofia"
import {
  BetaBadge,
  cn,
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@coval/ui"
import { Settings } from "lucide-react"

import {
  buildWorkspacePath,
  parseLegacyOrganizationRoute,
  parseWorkspaceRoute,
} from "@/utils/workspaceRoutes"

import { useWorkspaceContext } from "../contexts/WorkspaceContext"

/**
 * Sentinel value for the Configure menu item, which navigates to the
 * organization's workspace settings instead of switching workspaces.
 */
export const WORKSPACE_CONFIGURE_ITEM_VALUE = "__workspace-configure__"

interface WorkspaceSwitcherProps {
  className?: string
}

export function WorkspaceSwitcher({ className }: WorkspaceSwitcherProps) {
  const router = useRouter()
  const { currentWorkspace, workspaces } = useWorkspaceContext()

  // Legacy organization routes carry a workspace too, the organization's default
  // one, so the control belongs on both URL forms. Selecting from a legacy route
  // moves to the canonical path for the chosen workspace.
  const route =
    parseWorkspaceRoute(router.asPath) ??
    parseLegacyOrganizationRoute(router.asPath)

  if (!currentWorkspace || !route) {
    return null
  }

  const activeWorkspaces = workspaces.filter((w) => w.status === "ACTIVE")

  const handleWorkspaceChange = (selectedWorkspaceId: string) => {
    if (selectedWorkspaceId === WORKSPACE_CONFIGURE_ITEM_VALUE) {
      void router.push(`/${route.organizationSlug}/settings/workspaces`)
      return
    }

    if (selectedWorkspaceId === currentWorkspace.id) {
      return
    }

    const url = new URL(router.asPath, "https://coval.local")
    url.pathname = buildWorkspacePath(
      route.organizationSlug,
      selectedWorkspaceId,
      route.relativePath,
    )

    // Drop the query string: it can carry workspace-scoped state (filters,
    // a resource id opened in a drawer, ...) that belongs to the workspace
    // being left, not the one being switched to. Keep the hash, which is
    // typically in-page UI state (a section anchor) rather than data scope.
    // The Sofia Home thread selector is the one known exception — it isn't
    // workspace-scoped, and sofiaHome.ts's own path builder preserves it
    // across a workspace path change, so dropping it here would strand the
    // user's open thread on a bare Home landing.
    const sofiaThreadId = url.searchParams.get(SOFIA_THREAD_QUERY_PARAM)
    const preservedQuery = sofiaThreadId
      ? `?${SOFIA_THREAD_QUERY_PARAM}=${encodeURIComponent(sofiaThreadId)}`
      : ""
    void router.push(url.pathname + preservedQuery + url.hash)
  }

  return (
    <Select value={currentWorkspace.id} onValueChange={handleWorkspaceChange}>
      <SelectTrigger
        aria-label="Switch workspace"
        data-testid="workspace-switcher"
        className={cn("h-[28px] w-full", className)}
      >
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="min-w-0 truncate">
            <SelectValue placeholder="Workspace" />
          </span>
          <BetaBadge />
        </span>
      </SelectTrigger>
      <SelectContent>
        {activeWorkspaces.map((workspace) => (
          <SelectItem key={workspace.id} value={workspace.id}>
            {workspace.display_name}
          </SelectItem>
        ))}
        <SelectSeparator />
        <SelectItem
          value={WORKSPACE_CONFIGURE_ITEM_VALUE}
          icon={<Settings size={14} />}
        >
          Configure
        </SelectItem>
      </SelectContent>
    </Select>
  )
}
